import * as React from "react"
import { Head, router } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { FlashNotice } from "@/components/admin/FlashNotice"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table"

type GridPerson = {
  id: number
  name: string
  email: string | null
  department: string | null
  organization: string | null
  placeholder: boolean
  on_the_grid: boolean
  report_count: number
  last_week: string | null
}

const DESCRIPTION =
  "Choose who the Weekly Reports compliance grid answers for. Switching someone off hides them from the grid and changes nothing else."

export default function AdminWeeklyReportsIndex({
  people,
  on_the_grid,
}: {
  people: GridPerson[]
  on_the_grid: number
}) {
  const [selected, setSelected] = React.useState<number[]>([])

  const toggleSelected = (id: number) =>
    setSelected((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    )

  const allSelected = people.length > 0 && selected.length === people.length

  const setVisibility = (ids: number[], onTheGrid: boolean) => {
    router.patch(
      "/admin/weekly_reports/visibility",
      { user_ids: ids, files_weekly_reports: onTheGrid },
      { preserveScroll: true, onSuccess: () => setSelected([]) },
    )
  }

  return (
    <>
      <Head title="Weekly Reports">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Weekly Reports" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AdminShell>
        <PageHeader
          title="Weekly Reports"
          description={`${on_the_grid} of ${people.length} people appear on the compliance grid. Switching someone off hides them from it — their reports stay where they are, they can still read them, and they can still file.`}
        />

        <div className="mt-6">
          <FlashNotice />

          {/*
            The bar holds its height whether or not anything is selected, so
            checking a row does not shunt the whole table down the page.
          */}
          <div className="mb-4 flex min-h-9 flex-wrap items-center gap-3">
            {selected.length > 0 ? (
              <>
                <span className="text-sm text-ink-muted">
                  {selected.length} selected
                </span>
                <Button size="sm" variant="secondary" onClick={() => setVisibility(selected, true)}>
                  Show on the grid
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setVisibility(selected, false)}>
                  Hide from the grid
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelected([])}>
                  Clear
                </Button>
              </>
            ) : (
              <span className="text-sm text-ink-muted">
                Tick people to change several at once.
              </span>
            )}
          </div>

          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>
                  <Checkbox
                    aria-label={allSelected ? "Deselect everyone" : "Select everyone"}
                    checked={allSelected}
                    onChange={() =>
                      setSelected(allSelected ? [] : people.map((person) => person.id))
                    }
                  />
                </TableHeaderCell>
                <TableHeaderCell>Person</TableHeaderCell>
                <TableHeaderCell>Department</TableHeaderCell>
                <TableHeaderCell numeric>Reports</TableHeaderCell>
                <TableHeaderCell>Last filed</TableHeaderCell>
                <TableHeaderCell>On the grid</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {people.length === 0 && (
                <TableEmpty colSpan={6}>Nobody has filed a report yet.</TableEmpty>
              )}
              {people.map((person) => (
                <TableRow key={person.id}>
                  <TableCell>
                    <Checkbox
                      aria-label={`Select ${person.name}`}
                      checked={selected.includes(person.id)}
                      onChange={() => toggleSelected(person.id)}
                    />
                  </TableCell>
                  <TableCell nowrap>
                    <span className="flex items-center gap-2">
                      <span className="text-ink-display">{person.name}</span>
                      {person.placeholder && <Badge tone="muted">Never signed in</Badge>}
                    </span>
                    {person.email && (
                      <span className="block text-ink-muted">{person.email}</span>
                    )}
                  </TableCell>
                  <TableCell nowrap>
                    {person.department ?? <span className="text-ink-muted">Unassigned</span>}
                  </TableCell>
                  <TableCell numeric>{person.report_count}</TableCell>
                  <TableCell nowrap>
                    {person.last_week ? (
                      formatWeek(person.last_week)
                    ) : (
                      <span className="text-ink-muted">Never</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Checkbox
                      aria-label={`Show ${person.name} on the compliance grid`}
                      checked={person.on_the_grid}
                      onChange={(event) =>
                        setVisibility([person.id], event.target.checked)
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <p className="mt-3 text-sm text-ink-muted">
            Everyone IUPAT-level is here, along with anyone who has ever filed a
            report — so there is no row on the grid that cannot be switched off
            from this page.
          </p>
        </div>
      </AdminShell>
    </>
  )
}

function formatWeek(value: string) {
  return new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
    dateStyle: "medium",
  })
}
